import React, { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import PaymentProcessor from './PaymentProcessor';

interface Order {
  id: string;
  order_number: string;
  customer_name: string;
  customer_phone?: string;
  table_number?: number;
  items: any[];
  total_amount: number;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'completed';
  payment_status: 'unpaid' | 'paid';
  payment_method?: string;
  created_at: string;
  updated_at: string;
  notes?: string;
}

export default function OrderQueue() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'confirmed' | 'preparing' | 'ready'>('all');

  const fetchOrders = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) {
      setRefreshing(true);
    }
    
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        // Filter out completed orders
        const activeOrders = result.orders.filter((order: Order) => order.status !== 'completed');
        setOrders(activeOrders);
        
        if (showRefreshIndicator) {
          // Show success notification for manual refresh
          const notification = document.createElement('div');
          notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
          notification.innerHTML = `
            <i class="ri-check-line w-4 h-4 flex items-center justify-center"></i>
            <span>Data pesanan berhasil diperbarui!</span>
          `;
          document.body.appendChild(notification);
          
          setTimeout(() => {
            notification.remove();
          }, 3000);
        }
      } else {
        console.error('Failed to fetch orders:', result.error);
        if (showRefreshIndicator) {
          alert('❌ Gagal memuat data pesanan. Silakan coba lagi.');
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (showRefreshIndicator) {
        alert('❌ Terjadi kesalahan saat memuat data. Periksa koneksi internet Anda.');
      }
    } finally {
      if (showRefreshIndicator) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchOrders();
    
    // Auto refresh every 30 seconds
    const interval = setInterval(() => fetchOrders(), 30000);
    return () => clearInterval(interval);
  }, []);

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchOrders(true);
  };

  const updateOrderStatus = async (orderId: string, newStatus: Order['status']) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          orderId,
          status: newStatus,
          updated_at: new Date().toISOString()
        })
      });

      const result = await response.json();

      if (result.success) {
        // Update local state
        setOrders(prev => prev.map(order => 
          order.id === orderId 
            ? { ...order, status: newStatus, updated_at: new Date().toISOString() }
            : order
        ));

        // Show different messages based on status
        const statusMessages = {
          confirmed: '✅ Pesanan dikonfirmasi dan dikirim ke dapur!',
          preparing: '👨‍🍳 Status diubah ke "Sedang Dimasak"',
          ready: '🍽️ Pesanan siap disajikan ke customer!',
          completed: '✅ Pesanan telah selesai!'
        };

        // Show success notification instead of alert
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        notification.innerHTML = `
          <i class="ri-check-line w-4 h-4 flex items-center justify-center"></i>
          <span>${statusMessages[newStatus] || 'Status pesanan berhasil diupdate!'}</span>
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 4000);

      } else {
        alert(`Gagal mengupdate status: ${result.error}`);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Terjadi kesalahan saat mengupdate status pesanan');
    }
  };

  // Fungsi khusus untuk tombol "Mulai Masak" dengan konfirmasi dan notifikasi
  const handleStartCooking = async (orderId: string, orderNumber: string, customerName: string) => {
    const confirmed = confirm(
      `🍳 Mulai Masak Pesanan\n\n` +
      `Order: ${orderNumber}\n` +
      `Customer: ${customerName}\n\n` +
      `Pesanan akan dipindahkan ke status "Sedang Dimasak" dan dikirim ke dapur untuk diproses.\n\n` +
      `Lanjutkan?`
    );

    if (confirmed) {
      // Show cooking start notification
      const startNotification = document.createElement('div');
      startNotification.className = 'fixed top-4 right-4 bg-orange-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      startNotification.innerHTML = `
        <i class="ri-fire-line w-4 h-4 flex items-center justify-center"></i>
        <span>Memulai proses memasak...</span>
      `;
      document.body.appendChild(startNotification);

      setTimeout(() => {
        if (document.body.contains(startNotification)) {
          document.body.removeChild(startNotification);
        }
      }, 2000);

      // Update status to preparing
      await updateOrderStatus(orderId, 'preparing');
    }
  };

  // Fungsi khusus untuk tombol "Siap Saji" dengan konfirmasi dan notifikasi
  const handleMarkReady = async (orderId: string, orderNumber: string, customerName: string, tableNumber?: number) => {
    const confirmed = confirm(
      `🍽️ Tandai Pesanan Siap Saji\n\n` +
      `Order: ${orderNumber}\n` +
      `Customer: ${customerName}\n` +
      `${tableNumber ? `Meja: ${tableNumber}\n` : ''}` +
      `Pesanan akan dipindahkan ke status "Siap Disajikan" dan siap untuk diantarkan ke customer.\n\n` +
      `Pastikan semua menu sudah selesai dimasak dan siap disajikan.\n\n` +
      `Lanjutkan?`
    );

    if (confirmed) {
      // Show ready notification
      const readyNotification = document.createElement('div');
      readyNotification.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      readyNotification.innerHTML = `
        <i class="ri-restaurant-line w-4 h-4 flex items-center justify-center"></i>
        <span>Menandai pesanan siap saji...</span>
      `;
      document.body.appendChild(readyNotification);

      setTimeout(() => {
        if (document.body.contains(readyNotification)) {
          document.body.removeChild(readyNotification);
        }
      }, 2000);

      // Update status to ready
      await updateOrderStatus(orderId, 'ready');
    }
  };

  const markOrderCompleted = async (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    const confirmation = confirm(
      `Yakin ingin menyelesaikan pesanan ini?\n\n` +
      `Order: ${order.order_number}\n` +
      `Customer: ${order.customer_name}\n` +
      `Meja: ${order.table_number || 'N/A'}\n` +
      `Total: Rp ${order.total_amount.toLocaleString('id-ID')}\n\n` +
      `Pesanan akan dihapus dari antrian aktif.`
    );

    if (confirmation) {
      await updateOrderStatus(orderId, 'completed');
      
      // Remove from local state since completed orders are filtered out
      setOrders(prev => prev.filter(order => order.id !== orderId));
    }
  };

  const handlePayment = (order: Order) => {
    // Validasi data order lebih ketat
    console.log('handlePayment called with order:', order);
    
    // Pastikan order object valid
    if (!order) {
      alert('❌ Error: Data pesanan tidak ditemukan. Silakan refresh halaman.');
      return;
    }

    // Pastikan semua field yang diperlukan ada
    if (!order.id || !order.order_number) {
      alert('❌ Error: Data pesanan tidak lengkap. Silakan refresh halaman.');
      return;
    }

    // SUPER STRICT VALIDATION untuk total_amount
    let orderTotal = 0;
    
    // Debug logging untuk troubleshooting
    console.log('Order total validation:', {
      original_total_amount: order.total_amount,
      type_of_total_amount: typeof order.total_amount,
      is_null: order.total_amount === null,
      is_undefined: order.total_amount === undefined,
      is_nan: isNaN(Number(order.total_amount)),
      order_items: order.items,
      raw_order_data: order
    });
    
    // 1. Cek apakah total_amount ada dan valid
    if (order.total_amount !== undefined && 
        order.total_amount !== null && 
        order.total_amount !== '' && 
        !isNaN(Number(order.total_amount))) {
      
      orderTotal = Number(order.total_amount);
      
      // Pastikan setelah konversi masih valid
      if (isNaN(orderTotal) || orderTotal <= 0 || !isFinite(orderTotal)) {
        orderTotal = 0;
      }
    }
    
    // 2. Jika total_amount tidak valid, hitung ulang dari items dengan validasi ketat
    if (orderTotal <= 0 && order.items && Array.isArray(order.items) && order.items.length > 0) {
      console.log('Recalculating total from items...');
      
      orderTotal = order.items.reduce((sum, item) => {
        // Validasi setiap item dengan ketat
        if (!item || typeof item !== 'object') {
          console.warn('Invalid item found:', item);
          return sum;
        }

        const itemPrice = (item && typeof item.price !== 'undefined' && item.price !== null) 
          ? Number(item.price) : 0;
        const itemQty = (item && typeof item.quantity !== 'undefined' && item.quantity !== null) 
          ? Number(item.quantity) : 0;
        
        // Pastikan price dan quantity valid
        const validPrice = (isNaN(itemPrice) || itemPrice < 0) ? 0 : itemPrice;
        const validQty = (isNaN(itemQty) || itemQty < 0) ? 0 : itemQty;
        
        const itemTotal = validPrice * validQty;
        console.log(`Item calculation: ${item?.name || 'Unknown'} - ${validQty} x ${validPrice} = ${itemTotal}`);
        
        return sum + itemTotal;
      }, 0);
      
      console.log('Recalculated total:', orderTotal);
    }
    
    // 3. Jika masih tidak valid, coba fetch ulang data dari server
    if (!orderTotal || isNaN(orderTotal) || orderTotal <= 0 || !isFinite(orderTotal)) {
      console.error('CRITICAL: Final validation failed, attempting data refresh...');
      
      // Show loading notification
      const loadingNotification = document.createElement('div');
      loadingNotification.className = 'fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      loadingNotification.innerHTML = `
        <i class="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin"></i>
        <span>Memuat ulang data pesanan...</span>
      `;
      document.body.appendChild(loadingNotification);
      
      // Attempt to refresh order data
      fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/get-orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        }
      })
      .then(response => response.json())
      .then(result => {
        if (document.body.contains(loadingNotification)) {
          document.body.removeChild(loadingNotification);
        }
        
        if (result.success) {
          // Find the specific order
          const refreshedOrder = result.orders.find((o: Order) => o.id === order.id);
          
          if (refreshedOrder) {
            console.log('Found refreshed order:', refreshedOrder);
            
            // Validate refreshed order total
            let refreshedTotal = 0;
            if (refreshedOrder.total_amount && !isNaN(Number(refreshedOrder.total_amount)) && Number(refreshedOrder.total_amount) > 0) {
              refreshedTotal = Number(refreshedOrder.total_amount);
            } else if (refreshedOrder.items && Array.isArray(refreshedOrder.items)) {
              refreshedTotal = refreshedOrder.items.reduce((sum: number, item: any) => {
                const price = Number(item?.price || 0);
                const qty = Number(item?.quantity || 0);
                return sum + (price * qty);
              }, 0);
            }
            
            if (refreshedTotal > 0) {
              // Update local state dengan data yang benar
              setOrders(prev => prev.map(o => 
                o.id === refreshedOrder.id ? { ...refreshedOrder, total_amount: refreshedTotal } : o
              ));
              
              console.log('✅ Data berhasil di-refresh, mencoba pembayaran lagi...');
              
              // Retry payment with fresh data
              setTimeout(() => {
                handlePayment({ ...refreshedOrder, total_amount: refreshedTotal });
              }, 500);
              
              return;
            }
          }
        }
        
        // If refresh failed, show detailed error
        const errorDetails = {
          orderId: order.order_number || 'N/A',
          customerName: order.customer_name || 'N/A',
          originalTotal: String(order.total_amount),
          originalType: typeof order.total_amount,
          itemsCount: order.items?.length || 0,
          calculatedTotal: orderTotal,
          hasItems: !!(order.items && order.items.length > 0),
          itemsValid: order.items?.every((item: any) => item && typeof item === 'object' && item.price && item.quantity) || false
        };
        
        console.error('Complete error details:', errorDetails);
        
        const errorMsg = 
          '❌ ERROR: Data Pesanan Tidak Valid!\n\n' +
          `📋 Detail Error:\n` +
          `• Order ID: ${errorDetails.orderId}\n` +
          `• Customer: ${errorDetails.customerName}\n` +
          `• Total Asli: ${errorDetails.originalTotal} (${errorDetails.originalType})\n` +
          `• Jumlah Item: ${errorDetails.itemsCount}\n` +
          `• Total Hitung: ${errorDetails.calculatedTotal}\n` +
          `• Items Valid: ${errorDetails.itemsValid ? 'Ya' : 'Tidak'}\n\n` +
          `🔧 Kemungkinan Penyebab:\n` +
          `• Data pesanan rusak atau tidak lengkap\n` +
          `• Masalah sinkronisasi database\n` +
          `• Item pesanan hilang atau invalid\n\n` +
          `💡 Solusi:\n` +
          `1. Refresh halaman untuk memuat ulang data\n` +
          `2. Periksa koneksi internet\n` +
          `3. Hubungi admin jika masalah berlanjut`;
        
        alert(errorMsg);
        
        // Automatic refresh suggestion
        const shouldRefresh = confirm(
          '🔄 Refresh Otomatis?\n\n' +
          'Ingin mencoba refresh halaman untuk memperbaiki data pesanan?\n\n' +
          'Klik OK untuk refresh halaman secara otomatis.'
        );
        
        if (shouldRefresh) {
          window.location.reload();
        }
      })
      .catch(error => {
        console.error('Failed to refresh data:', error);
        
        if (document.body.contains(loadingNotification)) {
          document.body.removeChild(loadingNotification);
        }
        
        alert(
          '❌ Gagal Memuat Data!\n\n' +
          `Error: ${error.message}\n\n` +
          'Silakan:\n' +
          '• Refresh halaman secara manual\n' +
          '• Periksa koneksi internet\n' +
          '• Hubungi admin jika masalah berlanjut'
        );
      });
      
      return;
    }

    // Validasi customer name
    const customerName = order.customer_name || 'Guest';
    
    console.log('✅ Payment validation PASSED:', {
      orderId: order.order_number,
      orderTotal,
      customerName,
      originalData: order.total_amount,
      finalValidation: {
        isNumber: typeof orderTotal === 'number',
        isFinite: isFinite(orderTotal),
        isPositive: orderTotal > 0,
        isNotNaN: !isNaN(orderTotal)
      }
    });
    
    // CRITICAL: Pastikan data yang dikirim ke PaymentProcessor benar-benar valid
    const validatedOrder = {
      ...order,
      total_amount: orderTotal, // Gunakan hasil validasi yang pasti valid
      customer_name: customerName
    };
    
    // Double check sebelum set state
    if (isNaN(validatedOrder.total_amount) || validatedOrder.total_amount <= 0) {
      console.error('CRITICAL ERROR: Validated order still invalid!', validatedOrder);
      alert('❌ SISTEM ERROR: Data tidak dapat divalidasi. Hubungi admin segera!');
      return;
    }
    
    console.log('✅ Opening PaymentProcessor with validated data:', {
      orderId: validatedOrder.order_number,
      total: validatedOrder.total_amount,
      customer: validatedOrder.customer_name
    });
    
    setSelectedOrder(validatedOrder);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = async (paymentData: any) => {
    if (!selectedOrder) return;

    console.log('🔄 Memproses pembayaran selesai:', {
      selectedOrderId: selectedOrder.id,
      paymentMethod: paymentData.method,
      amount: paymentData.amount
    });

    // Show processing notification
    const processingNotification = document.createElement('div');
    processingNotification.className = 'fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
    processingNotification.innerHTML = `
      <i class="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin"></i>
      <span>Menyimpan status pembayaran...</span>
    `;
    document.body.appendChild(processingNotification);

    try {
      // Update payment status in database
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/update-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          orderId: selectedOrder.id,
          paymentStatus: 'paid', // Perbaiki field name
          paymentMethod: paymentData.method, // Perbaiki field name
          updated_at: new Date().toISOString()
        })
      });

      const result = await response.json();

      // Remove processing notification
      if (document.body.contains(processingNotification)) {
        document.body.removeChild(processingNotification);
      }

      if (result.success) {
        console.log('✅ Database update berhasil');
        
        // Update local state dengan status pembayaran yang benar
        setOrders(prev => prev.map(order => 
          order.id === selectedOrder.id 
            ? { 
                ...order, 
                payment_status: 'paid' as const, // Pastikan tipe yang benar
                payment_method: paymentData.method,
                updated_at: new Date().toISOString() 
              }
            : order
        ));

        // Show success notification
        const successNotification = document.createElement('div');
        successNotification.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        successNotification.innerHTML = `
          <i class="ri-check-line w-4 h-4 flex items-center justify-center"></i>
          <span>💰 Pembayaran berhasil disimpan! Status pesanan diperbarui.</span>
        `;
        document.body.appendChild(successNotification);
        
        setTimeout(() => {
          if (document.body.contains(successNotification)) {
            document.body.removeChild(successNotification);
          }
        }, 4000);

        // Close payment modal setelah berhasil
        setShowPaymentModal(false);
        setSelectedOrder(null);

        // Force refresh data untuk memastikan sinkronisasi
        setTimeout(() => {
          fetchOrders();
        }, 1000);

      } else {
        console.error('❌ Database update gagal:', result.error);
        
        // Show error notification
        const errorNotification = document.createElement('div');
        errorNotification.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        errorNotification.innerHTML = `
          <i class="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
          <span>❌ Gagal menyimpan status pembayaran: ${result.error}</span>
        `;
        document.body.appendChild(errorNotification);
        
        setTimeout(() => {
          if (document.body.contains(errorNotification)) {
            document.body.removeChild(errorNotification);
          }
        }, 5000);
        
        // Jangan tutup modal jika gagal
        alert(`❌ Gagal menyimpan status pembayaran!\n\nError: ${result.error}\n\nSilakan coba lagi atau hubungi admin.`);
      }
    } catch (error) {
      console.error('❌ Error updating payment status:', error);
      
      // Remove processing notification
      if (document.body.contains(processingNotification)) {
        document.body.removeChild(processingNotification);
      }
      
      // Show error notification
      const errorNotification = document.createElement('div');
      errorNotification.className = 'fixed top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      errorNotification.innerHTML = `
        <i class="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
        <span>❌ Terjadi kesalahan sistem saat menyimpan pembayaran</span>
      `;
      document.body.appendChild(errorNotification);
      
      setTimeout(() => {
        if (document.body.contains(errorNotification)) {
          document.body.removeChild(errorNotification);
        }
      }, 5000);
      
      alert(`❌ Terjadi kesalahan sistem!\n\nError: ${error.message}\n\nSilakan coba lagi atau refresh halaman.`);
    }
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'confirmed': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'preparing': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'ready': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'completed': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusText = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'Menunggu Konfirmasi';
      case 'confirmed': return 'Dikonfirmasi';
      case 'preparing': return 'Sedang Dimasak';
      case 'ready': return 'Siap Disajikan';
      case 'completed': return 'Selesai';
      default: return 'Unknown';
    }
  };

  const getNextStatus = (currentStatus: Order['status']): Order['status'] | null => {
    switch (currentStatus) {
      case 'pending': return 'confirmed';
      case 'confirmed': return 'preparing';
      case 'preparing': return 'ready';
      default: return null;
    }
  };

  const getNextStatusText = (currentStatus: Order['status']): string => {
    const nextStatus = getNextStatus(currentStatus);
    if (!nextStatus) return '';
    
    switch (nextStatus) {
      case 'confirmed': return 'Konfirmasi';
      case 'preparing': return 'Mulai Masak';
      case 'ready': return 'Siap Saji';
      default: return '';
    }
  };

  // Get order counts for filter buttons
  const getOrderCounts = () => {
    return {
      all: orders.length,
      pending: orders.filter(o => o.status === 'pending').length,
      confirmed: orders.filter(o => o.status === 'confirmed').length,
      preparing: orders.filter(o => o.status === 'preparing').length,
      ready: orders.filter(o => o.status === 'ready').length
    };
  };

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(order => order.status === statusFilter);

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center space-x-2 text-white">
          <i className="ri-loader-4-line text-2xl animate-spin w-6 h-6 flex items-center justify-center"></i>
          <span>Memuat pesanan...</span>
        </div>
      </div>
    );
  }

  const counts = getOrderCounts();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-white mb-2">Antrian Pesanan</h3>
          <p className="text-gray-300">Kelola dan proses pesanan yang masuk</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleManualRefresh}
          disabled={refreshing}
          className="whitespace-nowrap cursor-pointer"
        >
          {refreshing ? (
            <>
              <i className="ri-loader-4-line mr-2 w-4 h-4 flex items-center justify-center animate-spin"></i>
              Memuat...
            </>
          ) : (
            <>
              <i className="ri-refresh-line mr-2 w-4 h-4 flex items-center justify-center"></i>
              Refresh
            </>
          )}
        </Button>
      </div>

      {/* Status Filter */}
      <div className="flex flex-wrap gap-2">
        {[
          { id: 'all', label: 'Semua', count: counts.all },
          { id: 'pending', label: 'Pending', count: counts.pending },
          { id: 'confirmed', label: 'Dikonfirmasi', count: counts.confirmed },
          { id: 'preparing', label: 'Dimasak', count: counts.preparing },
          { id: 'ready', label: 'Siap', count: counts.ready }
        ].map((filter) => (
          <button
            key={filter.id}
            onClick={() => setStatusFilter(filter.id as any)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
              statusFilter === filter.id
                ? 'bg-red-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {filter.label} ({filter.count})
          </button>
        ))}
      </div>

      {/* Orders Grid */}
      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredOrders.map((order) => (
          <div
            key={order.id}
            className={`border-2 rounded-3xl p-6 transition-all duration-300 ${getStatusColor(order.status)} border`}
          >
            {/* Order Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-white">{order.order_number}</h3>
                <p className="text-gray-300 text-sm">{order.customer_name}</p>
                {order.table_number && (
                  <p className="text-gray-400 text-xs">Meja {order.table_number}</p>
                )}
              </div>
              <div className="text-right">
                <div className={`px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(order.status)}`}>
                  {getStatusText(order.status)}
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-bold mt-2 ${
                  order.payment_status === 'paid' 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                    : 'bg-red-500/20 text-red-400 border border-red-500/30'
                }`}>
                  {order.payment_status === 'paid' ? 'LUNAS' : 'BELUM BAYAR'}
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="mb-4">
              <h4 className="text-white font-semibold text-sm mb-2">Items:</h4>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-300">{item.quantity}x {item.name}</span>
                    <span className="text-white">Rp {(item.price * item.quantity).toLocaleString('id-ID')}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Total */}
            <div className="flex items-center justify-between mb-4 pt-2 border-t border-gray-600">
              <span className="text-white font-bold">Total:</span>
              <span className="text-yellow-400 font-bold text-lg">
                Rp {order.total_amount.toLocaleString('id-ID')}
              </span>
            </div>

            {/* Order Time */}
            <div className="text-xs text-gray-400 mb-4">
              <div>Dibuat: {new Date(order.created_at).toLocaleString('id-ID')}</div>
              <div>Update: {new Date(order.updated_at).toLocaleString('id-ID')}</div>
            </div>

            {/* Notes */}
            {order.notes && (
              <div className="mb-4 p-2 bg-gray-800/50 rounded-lg">
                <p className="text-gray-300 text-xs">
                  <span className="font-semibold">Catatan:</span> {order.notes}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2">
              {/* Status Update Button */}
              {getNextStatus(order.status) && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    const nextStatus = getNextStatus(order.status);
                    
                    // Khusus untuk tombol "Mulai Masak" (confirmed -> preparing)
                    if (order.status === 'confirmed' && nextStatus === 'preparing') {
                      handleStartCooking(order.id, order.order_number, order.customer_name);
                    }
                    // Khusus untuk tombol "Siap Saji" (preparing -> ready)
                    else if (order.status === 'preparing' && nextStatus === 'ready') {
                      handleMarkReady(order.id, order.order_number, order.customer_name, order.table_number);
                    }
                    // Status update biasa untuk yang lain
                    else {
                      updateOrderStatus(order.id, nextStatus);
                    }
                  }}
                  className="w-full whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-right-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                  {getNextStatusText(order.status)}
                </Button>
              )}

              {/* Payment Button - Only show if order is ready and not paid yet */}
              {order.status === 'ready' && order.payment_status === 'unpaid' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePayment(order)}
                  className="w-full whitespace-nowrap cursor-pointer bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black shadow-lg hover:shadow-xl focus:ring-yellow-500 border-yellow-500"
                >
                  <i className="ri-money-dollar-circle-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                  Bayar Cash
                </Button>
              )}

              {/* Print Invoice Button - Only show for paid orders */}
              {order.payment_status === 'paid' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Create a mock payment data for printing
                    const paymentData = {
                      orderId: order.order_number,
                      method: order.payment_method || 'cash',
                      amount: order.total_amount,
                      timestamp: order.updated_at,
                      customerName: order.customer_name,
                      receiptNumber: `RCP-${order.order_number.replace('ORD-', '')}`
                    };

                    // Generate and print invoice
                    const invoiceContent = `
===========================================
           TAKEYA JAPANESE RESTAURANT
===========================================
Alamat: Jl. Sakura No. 123, Jakarta
Telp: (021) 1234-5678
Email: info@takeya.restaurant

-------------------------------------------
STRUK PEMBAYARAN
-------------------------------------------
No. Struk    : ${paymentData.receiptNumber}
Tanggal      : ${new Date(paymentData.timestamp).toLocaleDateString('id-ID')}
Waktu        : ${new Date(paymentData.timestamp).toLocaleTimeString('id-ID')}
Kasir        : Admin User
Customer     : ${paymentData.customerName}
${order.table_number ? `Meja         : ${order.table_number}` : ''}

-------------------------------------------
DETAIL PESANAN
-------------------------------------------
Order ID     : ${paymentData.orderId}
${order.items.map(item => 
  `${item.quantity}x ${item.name.padEnd(20)} Rp ${(item.price * item.quantity).toLocaleString('id-ID').padStart(10)}`
).join('\n')}

Total        : Rp ${paymentData.amount.toLocaleString('id-ID')}

-------------------------------------------
PEMBAYARAN
-------------------------------------------
Metode       : ${paymentData.method === 'cash' ? 'Cash' : 
                 paymentData.method === 'card' ? 'Kartu' : 'QRIS'}
Status       : LUNAS

-------------------------------------------
         TERIMA KASIH ATAS KUNJUNGAN
            ANDA DI TAKEYA RESTAURANT!
===========================================
                    `;

                    // Print invoice
                    const printWindow = window.open('', '_blank');
                    if (printWindow) {
                      printWindow.document.write(`
                        <html>
                          <head>
                            <title>Invoice - ${paymentData.receiptNumber}</title>
                            <style>
                              body { 
                                font-family: 'Courier New', monospace; 
                                font-size: 12px; 
                                line-height: 1.4;
                                margin: 20px;
                                white-space: pre-line;
                              }
                              @media print {
                                body { margin: 0; }
                              }
                            </style>
                          </head>
                          <body>
                            ${invoiceContent}
                          </body>
                        </html>
                      `);
                      printWindow.document.close();
                      printWindow.print();
                    }

                    alert('✅ Struk berhasil dicetak!');
                  }}
                  className="w-full whitespace-nowrap cursor-pointer border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                >
                  <i className="ri-printer-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                  Print Invoice
                </Button>
              )}

              {/* Complete Order Button - Only show for ready orders that are paid */}
              {order.status === 'ready' && order.payment_status === 'paid' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => markOrderCompleted(order.id)}
                  className="w-full whitespace-nowrap cursor-pointer border-green-500 text-green-400 hover:bg-green-500 hover:text-white"
                >
                  <i className="ri-check-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                  Selesai
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredOrders.length === 0 && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-shopping-bag-line text-gray-400 text-3xl w-10 h-10 flex items-center justify-center"></i>
          </div>
          <p className="text-gray-400 text-lg">
            {statusFilter === 'all' ? 'Tidak ada pesanan aktif' : `Tidak ada pesanan dengan status "${getStatusText(statusFilter as Order['status'])}"`}
          </p>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedOrder && (
        <PaymentProcessor
          orderId={selectedOrder.order_number}
          orderTotal={selectedOrder.total_amount || 0}
          customerName={selectedOrder.customer_name || 'Guest'}
          onPaymentComplete={handlePaymentComplete}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedOrder(null);
          }}
        />
      )}
    </div>
  );
}
